import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DatePicker from "react-datepicker";
import { addDays } from "date-fns";
import { useLocation } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";

import { StackedChart } from "../../Components/PagesComponents/Statistics/StackedChart/StackedChart";
import StatisticsListPopup from "../../Components/PagesComponents/Statistics/StatisticsListPopup/StatisticsListPopup";
import ReactTableWrapper from "../../Components/common/ReactTableWrapper/ReactTableWrapper";
import { getStoredPageSize } from "../../Components/common/PageSizeSelect/PageSizeSelect";
import axiosApi from "../../utils/axios";
import { statisticsHeader } from "../../utils/constants/tableHeaders";
import CustomSelect from "../../Components/common/CustomSelect/CustomSelect";
import Warning from "../../Components/common/Warning/Warning";
import CustomMultiSelect from "../../Components/common/CustomMultiSelect/CustomMultiSelect";
import {
  calculateTotalFileCount,
  filterCustomTableData,
  filterListPopupData,
  getLatestDataPoint,
} from "../../Components/PagesComponents/Statistics/utils/statisticsUtil";
import {
  RequestStatisticsSubmissions,
  SuccessStatisticsSubmissions,
} from "../../store/actions/analysisReportActions";

import "./styles.scss";

const FILTERING_TYPE = [
  {
    id: "daily",
    value: "Daily",
  },
  {
    id: "weekly",
    value: "Weekly",
  },
  {
    id: "monthly",
    value: "Monthly",
  },
];

const SUBMIT_TYPE = [
  {
    value: "UI",
    label: "UI",
    selected: true,
  },
  {
    value: "email",
    label: "Email",
    selected: true,
  },
  {
    value: "share",
    label: "File",
    selected: true,
  },
  {
    value: "icapweb",
    label: "Proxy",
    selected: true,
  },
  {
    value: "api",
    label: "Public API",
    selected: true,
  },
];

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const STATISTICS_PAGE_SIZE = 10;

const DECISION_LABELS = {
  0: "Clean",
  1: "Suspicious",
  2: "Malicious",
};

const Statistics = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentUserRole = useSelector((state) => state?.users?.user?.role);
  const statisticsSubmissions = useSelector(
    (state) => state?.analysisReports?.statisticsSubmissions,
  );
  const statisticsPageCount = useSelector((state) => state?.analysisReports?.statisticsPageCount);
  const submissionsLoading = useSelector(
    (state) => state?.analysisReports?.statisticsSubmissionsLoading,
  );

  const [dataSet, setDataSet] = useState([]);
  const [dataSetLoading, setDataSetLoading] = useState(false);

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteringType, setFilteringType] = useState(FILTERING_TYPE[0]);
  const [filteredSubmitTypes, setFilteredSubmitTypes] = useState(SUBMIT_TYPE);

  const [listPopupActive, setListPopupActive] = useState(false);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationParams, setNotificationParams] = useState({
    endDate: null,
    startDate: null,
    filteringType: "Daily",
  });

  const [errorShow, setErrorShow] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [barStartDate, setBarStartDate] = useState();
  const [barEndDate, setBarEndDate] = useState();
  const [barDecision, setBarDecision] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    getStoredPageSize("statistics", STATISTICS_PAGE_SIZE),
  );
  const [tableFilters, setTableFilters] = useState([]);
  const [scrollToTable, setScrollToTable] = useState(false);

  const tableContainerRef = useRef(null);

  let query = useQuery();

  const optionButtonClickHandler = async () => {
    try {
      if (!startDate || !endDate) {
        setErrorText("Please fill in all the fields");
        setErrorShow(true);

        return;
      }
      setDataSetLoading(true);

      let result;

      const startDateYear = startDate.getFullYear();
      const startDateMonth =
        startDate.getMonth() < 9 ? `0${startDate.getMonth() + 1}` : startDate.getMonth() + 1;
      const startDateDay =
        startDate.getDate() < 10 ? `0${startDate.getDate()}` : startDate.getDate();
      const startDateParam = `${startDateYear}-${startDateMonth}-${startDateDay}`;

      const endDateYear = endDate.getFullYear();
      const endDateMonth =
        endDate.getMonth() < 9 ? `0${endDate.getMonth() + 1}` : endDate.getMonth() + 1;
      const endDateDay = endDate.getDate() < 10 ? `0${endDate.getDate()}` : endDate.getDate();
      const endDateParam = `${endDateYear}-${endDateMonth}-${endDateDay}`;

      const submitTypesString = filteredSubmitTypes
        .filter((submitType) => submitType.selected)
        .map((submitType) => submitType.value)
        .join(",");

      let requestUrl = `/api/reports/statistics/${filteringType.id}?StartDate=${startDateParam}&EndDate=${endDateParam}&SubmitTypes=${submitTypesString}`;

      if (currentUserRole === "ADMIN") {
        const usersString = filteredUsers
          .filter((user) => user.selected)
          .map((user) => user.value)
          .join(",");

        requestUrl = requestUrl + `&Users=${usersString}`;
      }

      result = await axiosApi.get(requestUrl);

      setDataSet(result.data);
      setDataSetLoading(false);
      setShowNotification(true);
      setNotificationParams({
        endDate: endDate,
        startDate: startDate,
        filteringType: filteringType.value,
      });
    } catch (error) {
      setDataSetLoading(false);
      console.log(error);
    }
  };

  const staticsSubmissionsRowOnClickHandler = (row) => {
    const url = row?.original?.file?.url || "";

    if (url) {
      history.push(url);
    }
  };

  const filterChangeHandler = (newFilters) => {
    // Table can fire the initial filter change before the default
    // date range is in state; skip to avoid an "undefined" request
    if (!barStartDate || !barEndDate) return;

    setTableFilters(newFilters);

    // The table has already moved back to the first page for the new search.
    dispatch(
      RequestStatisticsSubmissions({
        startDate: barStartDate,
        endDate: barEndDate,
        users: filteredUsers,
        submitTypes: filteredSubmitTypes,
        decision: barDecision,
        pageNumber: 1,
        pageSize,
        filters: newFilters,
      }),
    );
  };

  const dataPointSelectionHandler = (barStartDate, barEndDate, barDecision) => {
    setBarStartDate(barStartDate);
    setBarEndDate(barEndDate);
    setBarDecision(barDecision);
    setScrollToTable(true);
    setPageNumber(1);

    dispatch(
      RequestStatisticsSubmissions({
        startDate: barStartDate,
        endDate: barEndDate,
        users: filteredUsers,
        submitTypes: filteredSubmitTypes,
        decision: barDecision,
        pageNumber: 1,
        pageSize,
        filters: tableFilters,
      }),
    );
  };

  useEffect(() => {
    const getCurrentMonthsDailyData = async () => {
      try {
        setDataSetLoading(true);
        const today = new Date();

        // Thirty days when today is included.
        const thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 29));

        setEndDate(today);
        setStartDate(thirtyDaysAgo);

        const currentYear = `${today.getFullYear()}`;
        const currentMonth =
          today.getMonth() < 9 ? `0${today.getMonth() + 1}` : `${today.getMonth() + 1}`;
        const currentDay = today.getDate() < 10 ? `0${today.getDate()}` : `${today.getDate()}`;

        const priorYear = `${thirtyDaysAgo.getFullYear()}`;
        const priorMonth =
          thirtyDaysAgo.getMonth() < 9
            ? `0${thirtyDaysAgo.getMonth() + 1}`
            : `${thirtyDaysAgo.getMonth() + 1}`;
        const priorDay =
          thirtyDaysAgo.getDate() < 10
            ? `0${thirtyDaysAgo.getDate()}`
            : `${thirtyDaysAgo.getDate()}`;

        let result;
        let tempUsers = [];

        const submitTypesString = filteredSubmitTypes
          .filter((submitType) => submitType.selected)
          .map((submitType) => submitType.value)
          .join(",");

        if (currentUserRole === "ADMIN") {
          let usersString = "";
          const usersResponse = await axiosApi.get("/api/users/all");

          if (usersResponse.data && usersResponse.data.length > 0) {
            tempUsers = [...usersResponse.data];
            // Sort users alphabetically
            tempUsers.sort((a, b) => {
              const userA = a.username.toUpperCase();
              const userB = b.username.toUpperCase();

              return userA < userB ? -1 : userA > userB ? 1 : 0;
            });

            tempUsers = tempUsers.map((tempUser) => {
              const newTempUser = {
                value: tempUser.username,
                label: tempUser.username,
                selected: true,
              };

              return newTempUser;
            });

            usersString = tempUsers
              .filter((user) => user.selected)
              .map((user) => user.value)
              .join(",");
          }
          setFilteredUsers(tempUsers);

          result = await axiosApi.get(
            `/api/reports/statistics/daily?StartDate=${priorYear}-${priorMonth}-${priorDay}&EndDate=${currentYear}-${currentMonth}-${currentDay}&Users=${usersString}&SubmitTypes=${submitTypesString}`,
          );
        } else {
          result = await axiosApi.get(
            `/api/reports/statistics/daily?StartDate=${priorYear}-${priorMonth}-${priorDay}&EndDate=${currentYear}-${currentMonth}-${currentDay}&SubmitTypes=${submitTypesString}`,
          );
        }

        setDataSet(result.data);
        setDataSetLoading(false);
        // console.log(result.data);
        setNotificationParams({
          endDate: today,
          startDate: thirtyDaysAgo,
          filteringType: "Daily",
        });
        setShowNotification(true);

        // Fill the table as if the latest bar with data was clicked. Today can
        // be empty, so use the most recent day that actually has submissions.
        const latestDataPoint = getLatestDataPoint(result.data);

        if (latestDataPoint) {
          setBarStartDate(latestDataPoint.date);
          setBarEndDate(latestDataPoint.date);
          setBarDecision(latestDataPoint.decision);
          dispatch(
            RequestStatisticsSubmissions({
              startDate: latestDataPoint.date,
              endDate: latestDataPoint.date,
              users: tempUsers,
              submitTypes: filteredSubmitTypes,
              decision: latestDataPoint.decision,
              pageNumber: 1,
              pageSize,
            }),
          );
        }
      } catch (error) {
        setDataSetLoading(false);
        console.log(error);
      }
    };

    getCurrentMonthsDailyData();

    // When page switched reset statistics submissions
    return () => {
      dispatch(SuccessStatisticsSubmissions({ list: [], page_count: 0 }));
    };
  }, []);

  useEffect(() => {
    setScrollToTable(false);
    const delayDebounceFn = setTimeout(() => {
      if (!barStartDate || !barEndDate) return;
      dispatch(
        RequestStatisticsSubmissions({
          startDate: barStartDate,
          endDate: barEndDate,
          users: filteredUsers,
          submitTypes: filteredSubmitTypes,
          decision: barDecision,
          pageNumber: pageNumber < 1 ? 1 : pageNumber,
          pageSize,
          filters: tableFilters,
        }),
      );
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, pageSize]);

  useEffect(() => {
    if (statisticsSubmissions.length > 0 && scrollToTable) {
      tableContainerRef.current.scrollIntoView();
    }
  }, [statisticsSubmissions, scrollToTable]);

  useEffect(() => {
    if (query.get("data") !== null) {
      setListPopupActive(true);
    } else {
      setListPopupActive(false);
    }
  }, [query]);

  return (
    <div className="statistics">
      <div className="statistics__header">
        <h1>Statistics</h1>
        <p>This report shows the distribution of historical submissions between two dates.</p>
      </div>
      <div className="statistics__body">
        <div className="statistics__body__item">
          <div className="statistics__body__item__filter-area">
            <div className="statistics__body__item__filter-area__filter">
              <div className="statistics__body__item__filter-area__filter__header">Start Date</div>
              <div className="statistics__body__item__filter-area__filter__item">
                <DatePicker
                  selected={startDate}
                  closeOnScroll={true}
                  onChange={(date) => {
                    setStartDate(date);
                    setEndDate(null);
                  }}
                  selectsStart
                  showYearDropdown
                  showMonthDropdown
                  showDisabledMonthNavigation
                  isClearable
                  dateFormat={"dd MMM yyyy"}
                  className="statistics__body__item__filter-area__date-picker"
                />
              </div>
            </div>
            <div className="statistics__body__item__filter-area__filter">
              <div className="statistics__body__item__filter-area__filter__header">End Date</div>
              <div className="statistics__body__item__filter-area__filter__item">
                <DatePicker
                  selected={endDate}
                  closeOnScroll={true}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  minDate={startDate}
                  maxDate={
                    filteringType.id === "monthly"
                      ? addDays(startDate, 359)
                      : filteringType.id === "weekly"
                        ? addDays(startDate, 239)
                        : addDays(startDate, 119)
                  }
                  showYearDropdown
                  showMonthDropdown
                  showDisabledMonthNavigation
                  isClearable
                  dateFormat={"dd MMM yyyy"}
                  className="statistics__body__item__filter-area__date-picker"
                />
              </div>
            </div>
            {currentUserRole === "ADMIN" && (
              <div className="statistics__body__item__filter-area__filter">
                <div className="statistics__body__item__filter-area__filter__header">Users</div>
                <div className="statistics__body__item__filter-area__filter__item">
                  <CustomMultiSelect
                    options={filteredUsers}
                    setOptions={setFilteredUsers}
                    placeholder="Select..."
                  />
                </div>
              </div>
            )}
            <div className="statistics__body__item__filter-area__filter">
              <div className="statistics__body__item__filter-area__filter__header">Submit Type</div>
              <div className="statistics__body__item__filter-area__filter__item">
                <CustomMultiSelect
                  options={filteredSubmitTypes}
                  setOptions={setFilteredSubmitTypes}
                  placeholder="Select..."
                />
              </div>
            </div>
            <div className="statistics__body__item__filter-area__filter">
              <div className="statistics__body__item__filter-area__filter__header">Method</div>
              <div className="statistics__body__item__filter-area__filter__item">
                <CustomSelect
                  width="178px"
                  data={FILTERING_TYPE}
                  select={filteringType}
                  setSelected={setFilteringType}
                  height="36px"
                />
              </div>
            </div>
            <div className="statistics__body__item__filter-area__filter">
              <div className="statistics__body__item__filter-area__filter__header"></div>
              <div className="statistics__body__item__filter-area__filter__item">
                <button onClick={optionButtonClickHandler}>Search</button>
              </div>
            </div>
          </div>
          <StackedChart
            data={dataSet}
            dataLoading={dataSetLoading}
            filteringType={filteringType.id}
            startDate={startDate}
            endDate={endDate}
            dataPointSelectionHandler={dataPointSelectionHandler}
          />
          <div className="statistics__body__item__notification">
            {showNotification &&
              `Showing ${
                Math.floor(
                  (notificationParams.endDate.getTime() - notificationParams.startDate.getTime()) /
                    (1000 * 3600 * 24),
                ) + 1
              } days, grouped ${
                notificationParams.filteringType
              }. Total submission count is ${calculateTotalFileCount(dataSet)}`}
          </div>

          <div className="statistics__table border" ref={tableContainerRef}>
            {barStartDate && barEndDate ? (
              <p className="statistics__table__description">
                Details of {DECISION_LABELS[barDecision] || "All"} submissions{" "}
                {barStartDate === barEndDate
                  ? `on ${barStartDate}`
                  : `from ${barStartDate} to ${barEndDate}`}
              </p>
            ) : null}
            <div>
              <ReactTableWrapper
                data={filterCustomTableData(statisticsSubmissions)}
                isDataLoading={submissionsLoading}
                tableName="statistics"
                columns={statisticsHeader}
                minHeightRow="71"
                customPageSize={STATISTICS_PAGE_SIZE}
                rowOnClickHandler={staticsSubmissionsRowOnClickHandler}
                pagination={{
                  manualPagination: true,
                  totalPageCount: statisticsPageCount,
                  serverSidePaginationNumber: pageNumber,
                  setServerSidePaginationNumber: setPageNumber,
                  onPageSizeChange: setPageSize,
                }}
                onFilterChange={filterChangeHandler}
              />
            </div>
          </div>
        </div>
      </div>
      <StatisticsListPopup
        show={listPopupActive}
        groupID={query.get("data")}
        list={statisticsSubmissions}
        data={filterListPopupData(statisticsSubmissions, query.get("data"))}
      />
      <Warning
        onClickOk={() => setErrorShow(false)}
        show={errorShow}
        setShow={setErrorShow}
        text={errorText}
        article="Warning"
        cancelDisable={true}
        textOK="Okay"
      />
    </div>
  );
};

export default Statistics;
